import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';

interface Sale {
  id: string;
  user_id: string;
  product_id: string;
  product_type: string;
  amount: number;
  currency: string;
  status: string;
  payment_method: string;
  created_at: string;
  updated_at: string;
  metadata?: any;
  user?: {
    id: string;
    name: string;
    email: string;
    photos?: string[];
    profile_pic_url?: string;
  };
}

interface SalesStats {
  totalSales: number;
  totalRevenue: number;
  todaySales: number;
  todayRevenue: number;
  monthSales: number;
  monthRevenue: number;
}

const SalesPage: React.FC = () => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [stats, setStats] = useState<SalesStats>({
    totalSales: 0,
    totalRevenue: 0,
    todaySales: 0,
    todayRevenue: 0,
    monthSales: 0,
    monthRevenue: 0,
  });
  const [revenueByType, setRevenueByType] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('completed');
  const [selectedProductType, setSelectedProductType] = useState('all');

  const productTypes = [
    { value: 'all', label: 'Tümü' },
    { value: 'user_subscription', label: 'Kullanıcı Aboneliği' },
    { value: 'premium_subscription', label: 'Premium Abonelik' },
    { value: 'premium_plus_subscription', label: 'Sesli Arama Aboneliği' },
    { value: 'gift', label: 'Hediye' },
    { value: 'unknown', label: 'Bilinmeyen' },
  ];

  const statusOptions = [
    { value: 'completed', label: 'Tamamlandı' },
    { value: 'pending', label: 'Beklemede' },
    { value: 'cancelled', label: 'İptal Edildi' },
    { value: 'refunded', label: 'İade Edildi' },
  ];

  const formatCurrency = (amount: number, currency: string = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency || 'TRY',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Bilinmiyor';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Bilinmiyor';
    }
  };

  const getProductTypeDisplayName = (productType: string) => {
    const type = productTypes.find(t => t.value === productType);
    return type ? type.label : productType;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return '#28a745';
      case 'pending':
        return '#ffc107';
      case 'cancelled':
        return '#dc3545';
      case 'refunded':
        return '#6f42c1';
      default:
        return '#6c757d';
    }
  };

  const loadSalesData = useCallback(async () => {
    setLoading(true);
    try {
      console.log('🔄 Sales verisi yükleniyor... Status:', selectedStatus, 'Product Type:', selectedProductType);
      
      // Önce sales verilerini al
      let query = supabase
        .from('sales')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);

      // Status filtresi ekle
      if (selectedStatus !== 'all') {
        query = query.eq('status', selectedStatus);
      }

      // Product type filtresi ekle
      if (selectedProductType !== 'all') {
        query = query.eq('product_type', selectedProductType);
      }

      const { data: salesData, error } = await query;

      if (error) {
        console.error('❌ Supabase sales query error:', error);
        throw error;
      }

      console.log('✅ Sales query başarılı:', salesData?.length, 'kayıt');

      if (!salesData || salesData.length === 0) {
        setSales([]);
        await calculateStats();
        return;
      }

      // Tüm unique user_id'leri topla
      const uniqueUserIds = Array.from(new Set(
        salesData
          .map(sale => sale.user_id)
          .filter(id => id) // null/undefined'ları filtrele
      ));

      console.log('👥 Unique user sayısı:', uniqueUserIds.length);

      // Tek sorguda tüm user'ları çek
      let usersMap: Record<string, any> = {};
      
      if (uniqueUserIds.length > 0) {
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, name, email, photos, profile_pic_url')
          .in('id', uniqueUserIds);

        if (usersError) {
          console.error('❌ Users query error:', usersError);
        } else {
          console.log('✅ Users query başarılı:', usersData?.length, 'user bulundu');
          // Users'ları map'e dönüştür
          usersData?.forEach(user => {
            usersMap[user.id] = user;
          });
        }
      }

      // Sales verilerine user bilgilerini ekle
      const salesWithUsers = salesData.map(sale => ({
        ...sale,
        user: sale.user_id ? usersMap[sale.user_id] : null
      }));

      console.log('✅ Sales with users mapped:', salesWithUsers.length, 'kayıt');
      setSales(salesWithUsers);

      // İstatistikleri hesapla
      await calculateStats();

    } catch (error) {
      console.error('❌ Sales verilerini yükleme hatası:', error);
      setSales([]);
    } finally {
      setLoading(false);
    }
  }, [selectedStatus, selectedProductType]);

  const calculateStats = async () => {
    try {
      console.log('📊 İstatistikler hesaplanıyor...');
      
      // Tüm satışları al (status fark etmez)
      const { data: allSales, error } = await supabase
        .from('sales')
        .select('*');

      if (error) {
        console.error('❌ Stats calculation error:', error);
        return;
      }

      console.log('📊 Toplam sales verisi:', allSales?.length, 'kayıt');

      // Tamamlanmış satışları filtrele
      const completedSales = allSales?.filter((sale: Sale) => sale.status === 'completed') || [];
      console.log('✅ Tamamlanmış satışlar:', completedSales.length, 'kayıt');

      const totalSales = completedSales.length;
      const totalRevenue = completedSales.reduce((sum: number, sale: Sale) => sum + (sale.amount || 0), 0);

      // Bugünkü satışlar
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todaysales = completedSales.filter((sale: Sale) => {
        if (!sale.created_at) return false;
        const date = new Date(sale.created_at);
        return date >= today;
      });

      const todaySales = todaysales.length;
      const todayRevenue = todaysales.reduce((sum: number, sale: Sale) => sum + (sale.amount || 0), 0);

      // Bu ayın satışları
      const thisMonth = new Date();
      thisMonth.setDate(1);
      thisMonth.setHours(0, 0, 0, 0);
      const monthsales = completedSales.filter((sale: Sale) => {
        if (!sale.created_at) return false;
        const date = new Date(sale.created_at);
        return date >= thisMonth;
      });

      const monthSales = monthsales.length;
      const monthRevenue = monthsales.reduce((sum: number, sale: Sale) => sum + (sale.amount || 0), 0);

      console.log('📊 Hesaplanan istatistikler:', {
        totalSales,
        totalRevenue,
        todaySales,
        todayRevenue,
        monthSales,
        monthRevenue
      });

      setStats({
        totalSales,
        totalRevenue,
        todaySales,
        todayRevenue,
        monthSales,
        monthRevenue,
      });

      // Ürün tipine göre gelir hesapla
      const revenueByProductType: Record<string, number> = {};
      completedSales.forEach((sale: Sale) => {
        const type = sale.product_type || 'unknown';
        revenueByProductType[type] = (revenueByProductType[type] || 0) + (sale.amount || 0);
      });

      console.log('📊 Ürün tipine göre gelir:', revenueByProductType);
      setRevenueByType(revenueByProductType);

    } catch (error) {
      console.error('❌ İstatistik hesaplama hatası:', error);
    }
  };

  useEffect(() => {
    loadSalesData();
  }, [loadSalesData]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        flexDirection: 'column'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #F78D93',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '1rem'
        }}></div>
        <p style={{ color: '#666' }}>Satış verileri yükleniyor...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <h1 style={{ 
          color: '#2c3e50', 
          margin: 0,
          fontSize: '2rem',
          fontWeight: '600'
        }}>
          💰 Satış Yönetimi
        </h1>
        <button
          onClick={loadSalesData}
          style={{
            backgroundColor: '#F78D93',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          🔄 Yenile
        </button>
      </div>

      {/* İstatistik Kartları */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e3e6ea'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#4285f4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '1rem',
              fontSize: '20px'
            }}>
              🛒
            </div>
            <div>
              <h3 style={{ color: '#4285f4', margin: 0, fontSize: '0.9rem', textTransform: 'uppercase' }}>
                Toplam Satış
              </h3>
              <p style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '0.25rem 0', color: '#2c3e50' }}>
                {stats.totalSales}
              </p>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e3e6ea'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#34a853',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '1rem',
              fontSize: '20px'
            }}>
              💰
            </div>
            <div>
              <h3 style={{ color: '#34a853', margin: 0, fontSize: '0.9rem', textTransform: 'uppercase' }}>
                Toplam Gelir
              </h3>
              <p style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '0.25rem 0', color: '#2c3e50' }}>
                {formatCurrency(stats.totalRevenue, 'TRY')}
              </p>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e3e6ea'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#ff9800',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '1rem',
              fontSize: '20px'
            }}>
              📅
            </div>
            <div>
              <h3 style={{ color: '#ff9800', margin: 0, fontSize: '0.9rem', textTransform: 'uppercase' }}>
                Bugünkü Satış
              </h3>
              <p style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '0.25rem 0', color: '#2c3e50' }}>
                {stats.todaySales}
              </p>
              <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                {formatCurrency(stats.todayRevenue, 'TRY')}
              </p>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e3e6ea'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#9c27b0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '1rem',
              fontSize: '20px'
            }}>
              📊
            </div>
            <div>
              <h3 style={{ color: '#9c27b0', margin: 0, fontSize: '0.9rem', textTransform: 'uppercase' }}>
                Aylık Satış
              </h3>
              <p style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '0.25rem 0', color: '#2c3e50' }}>
                {stats.monthSales}
              </p>
              <p style={{ fontSize: '0.9rem', color: '#666', margin: 0 }}>
                {formatCurrency(stats.monthRevenue, 'TRY')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Ürün Tipine Göre Gelir */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e3e6ea',
        marginBottom: '2rem'
      }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '1.5rem' }}>📈 Ürün Tipine Göre Gelir</h3>
        <div style={{ display: 'grid', gap: '1rem' }}>
          {Object.entries(revenueByType).map(([type, revenue]) => (
            <div key={type} style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <span style={{ fontWeight: '500', color: '#2c3e50' }}>
                {getProductTypeDisplayName(type)}
              </span>
              <span style={{ fontWeight: 'bold', color: '#34a853' }}>
                {formatCurrency(revenue, 'TRY')}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Filtreler */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e3e6ea',
        marginBottom: '2rem'
      }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '1.5rem' }}>🔍 Filtreler</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#555' }}>
              Ürün Tipi
            </label>
            <select
              value={selectedProductType}
              onChange={(e) => setSelectedProductType(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e3e6ea',
                borderRadius: '8px',
                fontSize: '0.9rem'
              }}
            >
              {productTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#555' }}>
              Durum
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #e3e6ea',
                borderRadius: '8px',
                fontSize: '0.9rem'
              }}
            >
              {statusOptions.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Satış Listesi */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e3e6ea',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e3e6ea',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ color: '#2c3e50', margin: 0 }}>📋 Satış Listesi</h3>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>
            {sales.length} kayıt
          </span>
        </div>

        {sales.length === 0 ? (
          <div style={{
            padding: '3rem',
            textAlign: 'center',
            color: '#666'
          }}>
            Satış bulunamadı
          </div>
        ) : (
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#2c3e50' }}>
                    Kullanıcı
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#2c3e50' }}>
                    Ürün
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#2c3e50' }}>
                    Miktar
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#2c3e50' }}>
                    Durum
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', color: '#2c3e50' }}>
                    Tarih
                  </th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale, index) => (
                  <tr key={sale.id} style={{
                    borderBottom: '1px solid #e3e6ea',
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                  }}>
                    <td style={{ padding: '1rem', fontSize: '0.9rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {/* Profil Resmi */}
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          backgroundColor: '#f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {sale.user?.photos && sale.user.photos.length > 0 ? (
                            <img 
                              src={sale.user.photos[0]} 
                              alt={sale.user.name || 'User'}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                target.parentElement!.innerHTML = '<span style="color: #666; font-size: 0.8rem;">👤</span>';
                              }}
                            />
                          ) : sale.user?.profile_pic_url ? (
                            <img 
                              src={sale.user.profile_pic_url} 
                              alt={sale.user.name || 'User'}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                target.parentElement!.innerHTML = '<span style="color: #666; font-size: 0.8rem;">👤</span>';
                              }}
                            />
                          ) : (
                            <span style={{ color: '#666', fontSize: '1.2rem' }}>👤</span>
                          )}
                        </div>
                        
                        {/* Kullanıcı Bilgileri */}
                        <div>
                          <div style={{ fontWeight: '500', color: '#2c3e50', marginBottom: '2px' }}>
                            {sale.user?.name || 'Bilinmeyen Kullanıcı'}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>
                            {sale.user?.email || sale.user_id.substring(0, 8) + '...'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.9rem' }}>
                      <div style={{ fontWeight: '500', color: '#2c3e50' }}>
                        {getProductTypeDisplayName(sale.product_type)}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        {sale.product_id}
                      </div>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.9rem' }}>
                      <div style={{ fontWeight: 'bold', color: '#34a853' }}>
                        {formatCurrency(sale.amount, sale.currency)}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        {sale.currency}
                      </div>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.9rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        backgroundColor: getStatusColor(sale.status) + '20',
                        color: getStatusColor(sale.status)
                      }}>
                        {sale.status.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.9rem', color: '#666' }}>
                      {formatDate(sale.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesPage; 